/**
 * Organization Service
 * 사업자 관리 비즈니스 로직
 */

import { OrganizationRepository } from '../repositories';
import {
  Organization,
  isOrganizationPending,
  isOrganizationApproved,
  isOrganizationRejected,
  isOrganizationSuspended,
  getOrganizationStatusText,
  ServiceType,
} from '../models';

// Aliases for compatibility
const isPending = isOrganizationPending;
const isApproved = isOrganizationApproved;
const isRejected = isOrganizationRejected;
const isSuspended = isOrganizationSuspended;
const getStatusText = getOrganizationStatusText;

export interface ApprovalRequest {
  organizationId: string;
  reason?: string;
}

export interface RejectionRequest {
  organizationId: string;
  reason: string;
}

export class OrganizationService {
  constructor(private repository: OrganizationRepository) {}

  /**
   * 승인 대기 중인 사업자 목록 조회
   */
  async getPendingOrganizations(
    serviceType?: ServiceType
  ): Promise<Organization[]> {
    const organizations =
      await this.repository.getPendingOrganizations(serviceType);

    // 최신 신청 순으로 정렬
    return organizations.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }

  /**
   * 승인된 사업자 목록 조회
   */
  async getApprovedOrganizations(
    serviceType?: ServiceType
  ): Promise<Organization[]> {
    const organizations =
      await this.repository.getApprovedOrganizations(serviceType);

    // 최근 승인 순으로 정렬
    return organizations.sort((a, b) => {
      if (!a.approval_date || !b.approval_date) return 0;
      return (
        new Date(b.approval_date).getTime() -
        new Date(a.approval_date).getTime()
      );
    });
  }

  /**
   * 거부된 사업자 목록 조회
   */
  async getRejectedOrganizations(
    serviceType?: ServiceType
  ): Promise<Organization[]> {
    return await this.repository.getRejectedOrganizations(serviceType);
  }

  /**
   * 모든 사업자 목록 조회
   */
  async getAllOrganizations(serviceType?: ServiceType): Promise<Organization[]> {
    return await this.repository.getAllOrganizations(serviceType);
  }

  /**
   * 사업자 상세 조회
   */
  async getOrganization(id: string): Promise<Organization> {
    return await this.repository.getOrganizationById(id);
  }

  /**
   * 사업자 승인
   */
  async approveOrganization(request: ApprovalRequest): Promise<Organization> {
    if (!request.organizationId) {
      throw new Error('사업자 ID가 필요합니다.');
    }

    // 현재 상태 확인
    const org = await this.repository.getOrganizationById(request.organizationId);

    if (isApproved(org)) {
      throw new Error('이미 승인된 사업자입니다.');
    }

    if (isRejected(org)) {
      throw new Error('거부된 사업자는 승인할 수 없습니다.');
    }

    return await this.repository.approveOrganization({
      organization_id: request.organizationId,
    });
  }

  /**
   * 사업자 거부
   */
  async rejectOrganization(request: RejectionRequest): Promise<Organization> {
    if (!request.organizationId) {
      throw new Error('사업자 ID가 필요합니다.');
    }

    if (!request.reason || request.reason.trim().length === 0) {
      throw new Error('거부 사유를 입력해주세요.');
    }

    if (request.reason.length < 10) {
      throw new Error('거부 사유는 10자 이상 입력해주세요.');
    }

    // 현재 상태 확인
    const org = await this.repository.getOrganizationById(request.organizationId);

    if (isApproved(org)) {
      throw new Error('승인된 사업자는 거부할 수 없습니다.');
    }

    if (isRejected(org)) {
      throw new Error('이미 거부된 사업자입니다.');
    }

    return await this.repository.rejectOrganization({
      organization_id: request.organizationId,
      rejection_reason: request.reason,
    });
  }

  /**
   * 승인 대기 건수 조회
   */
  async getPendingCount(serviceType?: ServiceType): Promise<number> {
    if (serviceType) {
      const pending = await this.getPendingOrganizations(serviceType);
      return pending.length;
    }
    return await this.repository.getPendingCount();
  }

  /**
   * 사업자 상태별 통계
   */
  async getOrganizationStats(serviceType?: ServiceType): Promise<{
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    suspended: number;
  }> {
    const all = await this.getAllOrganizations(serviceType);

    return {
      total: all.length,
      pending: all.filter((org) => isPending(org)).length,
      approved: all.filter((org) => isApproved(org)).length,
      rejected: all.filter((org) => isRejected(org)).length,
      suspended: all.filter((org) => isSuspended(org)).length,
    };
  }

  /**
   * 사업자 검색
   */
  searchOrganizations(
    organizations: Organization[],
    query: string
  ): Organization[] {
    if (!query || query.trim().length === 0) {
      return organizations;
    }

    const lowerQuery = query.toLowerCase();

    return organizations.filter(
      (org) =>
        org.name.toLowerCase().includes(lowerQuery) ||
        org.business_number?.includes(query) ||
        org.phone?.includes(query) ||
        org.address?.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * 서비스 타입별 필터링
   */
  filterByServiceType(
    organizations: Organization[],
    serviceType: ServiceType
  ): Organization[] {
    return organizations.filter((org) => org.service_type === serviceType);
  }

  /**
   * 사용 중지된 사업자 목록 조회
   */
  async getSuspendedOrganizations(): Promise<Organization[]> {
    return await this.repository.getSuspendedOrganizations();
  }

  /**
   * 사업자 사용 중지
   */
  async suspendOrganization(organizationId: string, reason: string): Promise<boolean> {
    if (!organizationId) {
      throw new Error('사업자 ID가 필요합니다.');
    }

    if (!reason || reason.trim().length < 5) {
      throw new Error('사용 중지 사유를 5자 이상 입력해주세요.');
    }

    const result = await this.repository.suspendOrganization(organizationId, reason);
    return result.success;
  }

  /**
   * 사업자 재활성화
   */
  async reactivateOrganization(organizationId: string): Promise<boolean> {
    if (!organizationId) {
      throw new Error('사업자 ID가 필요합니다.');
    }

    const result = await this.repository.reactivateOrganization(organizationId);
    return result.success;
  }
}
